import { IBEError } from '@common/interfaces/be-error.interface';
import { UseQueryOptions } from '@common/interfaces/query-options.interface';
import {
    IApplyCouponRequest,
    ICouponCodeResponse,
    itemApiRequest,
    TItemV1CouponApplyPutError,
    TItemV1CouponCodeIdGetError,
    TItemV1CouponSetIdCouponCodePostError,
} from '@src/api/item-generated-api';
import { createCouponDto, ICouponDto } from '@src/api/model/coupon.dto';
import { createSubscriptionDto, ISubscriptionDto } from '@src/api/model/subscription.dto';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
// import { apiHooks } from '../api-hooks';

const NAME = 'coupon';

const useApplyCoupon = (): UseMutationResult<
    ISubscriptionDto,
    IBEError<TItemV1CouponApplyPutError>,
    IApplyCouponRequest
> => {
    return useMutation(
        (data) => {
            return itemApiRequest
                .itemV1CouponApplyPut(data, {})
                .then((r) =>
                    createSubscriptionDto({ s1: r.data.subscription, schedule: r.data.subscription_with_scheduled_changes, orderInfo: r.data.next_invoice || {} }),
                );
        },
        {
            mutationKey: `${NAME}-apply-coupon`,
        },
    );
};

const useGetCoupon = (
    id: string,
    options?: UseQueryOptions,
): UseQueryResult<ICouponDto, IBEError<TItemV1CouponCodeIdGetError>> => {
    return useQuery<ICouponDto, IBEError<TItemV1CouponCodeIdGetError>>(
        `${NAME}-${id}`,
        () => {
            return itemApiRequest.itemV1CouponCodeIdGet(encodeURIComponent(id)).then((res) => createCouponDto(res.data));
        },
        options,
    );
};

const useGetCouponById = (): UseMutationResult<ICouponDto, IBEError<TItemV1CouponCodeIdGetError>, string> => {
    return useMutation(
        (id) => {
            return itemApiRequest.itemV1CouponCodeIdGet(encodeURIComponent(id)).then((res) => createCouponDto(res.data));
        },
        {
            mutationKey: `${NAME}-get-coupon`,
        },
    );
};

const useGetCancellationCoupon = (
    queryOptions?: UseQueryOptions,
): UseQueryResult<ICouponCodeResponse, IBEError<TItemV1CouponCodeIdGetError>> => {
    return useQuery<ICouponCodeResponse, IBEError<TItemV1CouponCodeIdGetError>>(
        `${NAME}-cancellation-coupon`,
        () => {
            return itemApiRequest.itemV1CouponSetIdCouponCodeGet('cancellation').then((res) => res.data);
        },
        {
            ...queryOptions,
            enabled: queryOptions?.enabled !== undefined ? queryOptions.enabled : true,
        },
    );
};

const useCreateCancellationCoupon = (): UseMutationResult<
    ICouponCodeResponse,
    IBEError<TItemV1CouponSetIdCouponCodePostError>
> => {
    return useMutation(
        () => {
            return itemApiRequest.itemV1CouponSetIdCouponCodePost('cancellation').then((res) => res.data);
        },
        {
            mutationKey: `${NAME}-create-cancellation-coupon`,
        },
    );
};

export const coupon = {
    useGetCoupon,
    useApplyCoupon,
    useGetCouponById,
    useGetCancellationCoupon,
    useCreateCancellationCoupon,
};
